import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/stores/uiStore'
import {
  DISCOUNT_MESSAGES,
  fetchCustomerDiscounts,
  previewDiscount,
  spinDiscount,
  type CustomerDiscount,
} from '@/lib/api'

export type AppliedDiscount = {
  code: string
  label: string
  amountKobo: number
}

interface DiscountBoxProps {
  itemsKobo: number
  applied: AppliedDiscount | null
  onChange: (discount: AppliedDiscount | null) => void
}

function messageFor(error: string): string {
  return DISCOUNT_MESSAGES[error] ?? 'That code could not be applied.'
}

/**
 * Everything about paying less: the one-off mystery spin, any rewards the
 * account already holds, and a code field. Quotes come from the server, so the
 * figure shown here is the figure the order is placed at.
 */
export function DiscountBox({ itemsKobo, applied, onChange }: DiscountBoxProps) {
  const { isSignedIn } = useAuth()
  const addToast = useUIStore((s) => s.addToast)

  const [rewards, setRewards] = useState<CustomerDiscount[]>([])
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [spinning, setSpinning] = useState(false)

  useEffect(() => {
    if (!isSignedIn) return
    let cancelled = false
    fetchCustomerDiscounts().then((list) => {
      if (!cancelled) setRewards(list)
    })
    return () => {
      cancelled = true
    }
  }, [isSignedIn])

  const apply = async (rawCode: string) => {
    const trimmed = rawCode.trim()
    if (!trimmed) {
      setError(messageFor('discount_invalid'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await previewDiscount(trimmed, itemsKobo)
      if (!result.success) {
        setError(messageFor(result.error))
        return
      }
      onChange({
        code: result.discount.code,
        label: result.discount.label || result.discount.code,
        amountKobo: result.discount.amountKobo,
      })
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  const runSpin = async () => {
    setSpinning(true)
    setError(null)
    try {
      const result = await spinDiscount()
      if (!result.success) {
        setError(messageFor(result.error))
        return
      }
      const quoted = await previewDiscount(result.discount.code, itemsKobo)
      if (!quoted.success) {
        setError(messageFor(quoted.error))
        return
      }
      onChange({
        code: result.discount.code,
        label: result.discount.label,
        amountKobo: quoted.discount.amountKobo,
      })
      addToast(result.discount.label, 'success')
    } finally {
      setSpinning(false)
    }
  }

  const hasSpin = rewards.some((r) => r.source === 'spin')
  const unused = rewards.filter(
    (r) => r.available && r.source !== 'spin' && r.code !== applied?.code
  )
  const canSpin = isSignedIn && !hasSpin && !applied

  return (
    <section className="bg-wd-mist p-6">
      <h2 className="mb-4 font-display text-lg normal-case text-carbon">Discounts</h2>

      {canSpin && (
        <div className="mb-5 flex flex-col gap-3 border border-wd-orange/40 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-wd-orange">Mystery spin</p>
            <p className="mt-1 text-sm text-carbon/70">
              One free spin. Every result is a real discount.
            </p>
          </div>
          <Button variant="secondary" isLoading={spinning} onClick={runSpin}>
            Spin for a discount
          </Button>
        </div>
      )}

      {!applied && unused.length > 0 && (
        <div className="mb-5 space-y-2">
          {unused.map((reward) => (
            <div
              key={reward.id}
              className="flex items-center justify-between gap-3 border border-ink/10 bg-white px-4 py-3"
            >
              <span className="min-w-0 truncate text-sm text-carbon">{reward.label || reward.code}</span>
              <button
                type="button"
                className="shrink-0 text-xs uppercase tracking-[0.15em] text-carbon hover:underline"
                onClick={() => apply(reward.code)}
                disabled={busy}
              >
                Apply
              </button>
            </div>
          ))}
        </div>
      )}

      {applied ? (
        <div className="flex items-center justify-between gap-3 border border-ink/10 bg-white px-4 py-3">
          <span className="min-w-0 text-sm text-carbon">
            {applied.label}{' '}
            <span className="font-mono text-xs text-carbon/50">{applied.code}</span>
          </span>
          <button
            type="button"
            className="shrink-0 text-xs uppercase tracking-[0.15em] text-carbon/50 hover:text-wd-orange"
            onClick={() => {
              onChange(null)
              setError(null)
            }}
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="flex items-end gap-3">
          <Input
            label="Have a code?"
            placeholder="SKY-XXXXXX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                apply(code)
              }
            }}
          />
          <Button
            variant="secondary"
            isLoading={busy}
            className="shrink-0"
            onClick={() => apply(code)}
          >
            Apply
          </Button>
        </div>
      )}

      {error && <p className="mt-3 text-xs text-carbon">{error}</p>}

      {!isSignedIn && (
        <p className="mt-4 text-xs text-carbon/50">
          Sign in to use a reward from your streak or mystery spin.
        </p>
      )}
    </section>
  )
}
